import * as pdfjsLib from "pdfjs-dist";
import pdfjsWorker from "pdfjs-dist/build/pdf.worker.entry";

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

export type PdfLine = string | PdfLine[];

// Over how many horizontal pixels we need to pass to consider the second phrase is a new entry
const SAME_WORD_Y_THRESHOLD = 11;
const SAME_WORD_X_THRESHOLD = 0;
// Over how many vertibal pixels we need to pass to assume a new line/table entry had started
const SAME_LINE_Y_THRESHOLD = 10;
// If we jump more that 100px in X axis, we must have started a new line
const SAME_LINE_X_THRESHOLD = 100;

export const readPdfTableLines: (file: any) => Promise<PdfLine[]> = async (
  file,
) => {
  const fileContent = await new Promise((resolve, reject) => {
    try {
      console.log(file);
      const fileReader = new FileReader();
      fileReader.onloadend = (e) => resolve(fileReader.result);
      fileReader.onerror = (e) => reject(e);
      fileReader.readAsArrayBuffer(file);
    } catch (e) {
      reject(e);
    }
  });
  // console.log("readArrayBuffer:", fileContent);
  const pdf = await pdfjsLib.getDocument(fileContent).promise;

  const maxPages = pdf.numPages;
  let countPromises = []; // collecting all page promises

  for (let j = 1; j <= maxPages; j++) {
    const page = pdf.getPage(j);
    countPromises.push(
      page.then(function(page) {
        // add page promise
        const textContent = page.getTextContent();
        return textContent.then((text) => {
          let lastTransformYIndex = null;
          let prevXPos = 0;
          // used for reading line wraps
          let tempLineExpand = 0;
          let prevWidth = 0;
          // return content promise
          return text.items.reduce((acc, s, index, array) => {
            const currentYPos = s.transform.slice(-1).pop();
            const currentXPos = s.transform.slice(-2)[0];
            const currentWidth = s.width;

            // All completed lines up until now
            const prevLines = acc.slice(0, -1);
            // Current line (always the last element)
            let line = acc.slice(-1).pop() || [];

            // if we dont have a prevY position then it's the start of a line
            if (!lastTransformYIndex && lastTransformYIndex !== 0) {
              lastTransformYIndex = currentYPos;
            } else if (!prevXPos && prevXPos !== 0) {
              prevXPos = currentXPos;
            } else if (
              Math.abs(lastTransformYIndex - currentYPos) >
                SAME_LINE_Y_THRESHOLD &&
              Math.abs(currentXPos - prevXPos) > SAME_LINE_X_THRESHOLD
            ) {
              // otherwise, if the y position changed and X pos jumped, beyond our row threshold than let's establish a new line
              lastTransformYIndex = currentYPos;
              prevXPos = currentXPos;
              tempLineExpand = 0;
              // submit the line. continue accumulating with a new line
              return [...prevLines, line, [s.str]];
            }

            // if the x pos is included in the prev item - we know there is word wrapping
            // ex:| avi is
            //    | a nice
            //    | person
            //    |      e
            const isWordWrap =
              prevWidth + prevXPos >= currentXPos &&
              Math.abs((tempLineExpand || lastTransformYIndex) - currentYPos) <
                SAME_WORD_Y_THRESHOLD &&
              Math.abs((tempLineExpand || lastTransformYIndex) - currentYPos) >
                0;

            const isSameWordWithXGap =
              Math.abs(currentXPos - (prevXPos + prevWidth)) <
              SAME_WORD_X_THRESHOLD;
            // if x & y position has less than the same words thrashold,
            // or have word wrap treat it as same word
            if (isWordWrap || isSameWordWithXGap) {
              // Make sure to update current line jump
              tempLineExpand = isWordWrap ? currentYPos : 0;
              // read the last cluster in the line
              const word = line.slice(-1).pop();
              // remove it from the line
              const editedLine = line.slice(0, -1);
              // keep an array, so we wound have an indication this word was constructed unnaturally
              if (Array.isArray(word)) {
                editedLine.push([...word, s.str]);
              } else {
                // create a new array now, if it wasn't an array before
                editedLine.push([word, s.str]);
              }

              // make sure the new line is now "line"
              line = editedLine;
            } else {
              // otherwise just add the word to the end of the line no headache
              line = [...line, s.str];
            }
            // move cursor to current word position for next words
            prevXPos = currentXPos;
            prevWidth = currentWidth;
            // join back the line we are currently working on to the rest
            return [...prevLines, line];
          }, []);
        });
      }),
    );
  }

  const pages = await Promise.all(countPromises);

  // flatten - return as if it was one page
  return pages.reduce((acc, page) => [...acc, ...page], []);
};
